import React, { useState } from 'react';
import {
  TruckIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilSquareIcon,
  TrashIcon,
  WrenchScrewdriverIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';
import { useVehicles } from '../../hooks/useVehicles';
import { Vehicle, VehicleStatus } from '../../types/vehicle';
import VehicleEditor from './VehicleEditor';
import VehicleDetails from './VehicleDetails';
import { formatDate } from '../../utils/dateHelpers';
import CommonPageHeader from '../common/CommonPageHeader';
import SearchBar from '../common/SearchBar';
import LoadingScreen from '../common/LoadingScreen';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';

const Vehicles: React.FC = () => {
  const { t } = useLanguage();
  const { vehicles, loading, error, remove } = useVehicles();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<VehicleStatus | 'all'>('all');
  const [showEditor, setShowEditor] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [vehicleToDelete, setVehicleToDelete] = useState<Vehicle | null>(null);

  // Szűrés
  const filteredVehicles = vehicles.filter(vehicle => {
    const matchesSearch = 
      vehicle.licensePlate.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.make.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.customerName?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || vehicle.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const handleEdit = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setShowEditor(true);
  };

  const handleView = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setShowDetails(true);
  };

  const handleDelete = (vehicle: Vehicle) => {
    setVehicleToDelete(vehicle);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!vehicleToDelete) return;

    try {
      await remove(vehicleToDelete.id);
      toast.success(t('fleet.messages.deleted'));
      setShowDeleteModal(false);
      setVehicleToDelete(null);
    } catch (error: any) {
      toast.error(`${t('fleet.messages.error')}: ${error.message}`);
    }
  };

  const handleEditorClose = () => {
    setShowEditor(false);
    setSelectedVehicle(null);
  };

  const handleDetailsClose = () => {
    setShowDetails(false);
    setSelectedVehicle(null);
  };

  const getStatusBadge = (status: VehicleStatus) => {
    const badges = {
      active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      inactive: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      maintenance: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
      sold: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badges[status]}`}>
        {t(`fleet.status.${status}`)}
      </span>
    );
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-200">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <CommonPageHeader
        title={t('fleet.title')}
        subtitle={t('fleet.subtitle')}
        icon={TruckIcon}
      >
        <button
          onClick={() => {
            setSelectedVehicle(null);
            setShowEditor(true);
          }}
          className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          <span>{t('fleet.list.addVehicle')}</span>
        </button>
      </CommonPageHeader>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={t('fleet.list.search')}
            />
          </div>

          {/* Status Filter */}
          <div className="sm:w-48">
            <div className="relative">
              <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as VehicleStatus | 'all')}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">{t('fleet.list.allStatuses')}</option>
                <option value="active">{t('fleet.status.active')}</option>
                <option value="inactive">{t('fleet.status.inactive')}</option>
                <option value="maintenance">{t('fleet.status.maintenance')}</option>
                <option value="sold">{t('fleet.status.sold')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results count */}
        <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          {filteredVehicles.length} {t('fleet.list.resultsCount')}
        </div>
      </div>

      {/* Vehicles List */}
      {filteredVehicles.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
          <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">{t('fleet.list.noVehicles')}</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {searchTerm || statusFilter !== 'all' 
              ? t('fleet.list.noResults')
              : t('fleet.list.startAdding')}
          </p>
          {!searchTerm && statusFilter === 'all' && (
            <div className="mt-6">
              <button
                onClick={() => {
                  setSelectedVehicle(null);
                  setShowEditor(true);
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                {t('fleet.list.firstVehicle')}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  {t('fleet.columns.licensePlate')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  {t('fleet.columns.vehicle')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  {t('fleet.columns.mileage')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  {t('fleet.columns.owner')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Vezető
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  {t('fleet.columns.status')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  {t('fleet.columns.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredVehicles.map((vehicle) => (
                <tr 
                  key={vehicle.id} 
                  onClick={() => handleView(vehicle)}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {vehicle.photos && vehicle.photos.length > 0 ? (
                          <img
                            src={vehicle.photos[0].url}
                            alt={vehicle.licensePlate}
                            className="h-10 w-10 rounded object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                            <TruckIcon className="h-6 w-6 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {vehicle.licensePlate}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">{vehicle.make} {vehicle.model}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{vehicle.year}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {vehicle.mileage.toLocaleString()} km
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {vehicle.customerName || vehicle.ownerName || '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {vehicle.assignedToName || '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {getStatusBadge(vehicle.status)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleView(vehicle)}
                        className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                        title={t('fleet.actions.view')}
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleEdit(vehicle)}
                        className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                        title={t('fleet.actions.edit')}
                      >
                        <PencilSquareIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(vehicle)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        title={t('fleet.actions.delete')}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showEditor && (
        <VehicleEditor
          vehicle={selectedVehicle}
          onClose={handleEditorClose}
        />
      )}

      {showDetails && selectedVehicle && (
        <VehicleDetails
          vehicle={selectedVehicle}
          onClose={handleDetailsClose}
          onEdit={() => {
            setShowDetails(false);
            setShowEditor(true);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setVehicleToDelete(null);
        }}
        onConfirm={confirmDelete}
        title={t('fleet.actions.delete')}
        deleteMessage={t('fleet.actions.confirmDelete', { licensePlate: vehicleToDelete?.licensePlate || '' })}
        deleteWarning={t('fleet.messages.deleteWarning')}
        danger={true}
      />
    </div>
  );
};

export default Vehicles;
